import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Space,
  Tag,
  Button,
  Spin,
  Alert,
  Typography,
  List,
  Breadcrumb,
  Form,
  Dropdown,
  App,
} from 'antd';
import type { MenuProps } from 'antd';
import { withDevModalFilePath } from '../../components/common';
import { useFormAutoBackup } from '../../hooks/useFormAutoBackup';

const { Modal } = withDevModalFilePath('client/src/pages/WorkOrderTemplates/WorkOrderTemplateDetailPage.tsx');
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  FileTextOutlined,
  MoreOutlined,
  DeleteOutlined,
  CopyOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { PermissionButton } from '../../components/common/PermissionButton';
import { usePermission } from '../../hooks/usePermission';
import { useWorkOrderTemplates, useWorkOrderTemplateMutations } from '../../hooks/useWorkOrderTemplates';
import { WorkOrderTemplateForm, TemplatePartsSection, TemplateToolsSection } from '../../components/work-order';
import type { ChecklistItem, WorkOrderTemplate } from '../../../../shared/types/work-order-template.types';
import { UserDisplay } from '../../components/common/UserDisplay';
import type { UserDisplayProps } from '../../components/common/UserDisplay';
import { trpc } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const WORK_TYPE_LABEL: Record<string, string> = {
  inspection: '点検',
  repair: '修理',
  replacement: '交換',
  maintenance: 'メンテナンス'
};

const PRIORITY_LABEL: Record<string, string> = {
  low: '低',
  medium: '中',
  high: '高',
  urgent: '緊急'
};

const PRIORITY_COLOR: Record<string, string> = {
  low: 'green',
  medium: 'blue',
  high: 'orange',
  urgent: 'red'
};

const CHECKLIST_ITEM_TYPE_LABEL: Record<string, string> = {
  check: 'チェックボックス',
  text: 'テキスト',
  number: '数値',
  date: '日付',
  select: '選択肢'
};

type UserOptionSource = {
  id: string;
  name?: string | null;
  email?: string | null;
};

const isUserOptionSource = (value: unknown): value is UserOptionSource => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { id?: unknown };
  return typeof candidate.id === 'string';
};

type TemplateWithUserRelations = WorkOrderTemplate & {
  createdByUser?: UserDisplayProps['user'] | null;
  updatedByUser?: UserDisplayProps['user'] | null;
  workTypeCategory?: {
    id: string;
    categoryLabel?: string | null;
    color?: string | null;
    icon?: string | null;
  } | null;
};

const hasUserRelations = (value: unknown): value is TemplateWithUserRelations =>
  Boolean(
    value &&
    typeof value === 'object' &&
    ('createdByUser' in value || 'updatedByUser' in value)
  );

export function WorkOrderTemplateDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { canEdit, canManage, getDisabledMessage } = usePermission();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [form] = Form.useForm();
  const { user } = useAuthStore();

  // フォーム自動バックアップ（詳細ページは編集のみなので無効化）
  const { clearBackup } = useFormAutoBackup({
    formType: 'work_order_template',
    entityId: undefined,
    form,
    enabled: false, // 編集のみのページなのでバックアップは無効
  });

  const { templates, isLoading, refetch } = useWorkOrderTemplates({
    includeInactive: true,
    includeEquipments: false
  });

  const { updateTemplate, deleteTemplate, isUpdating } = useWorkOrderTemplateMutations();

  // 複製mutation
  const duplicateMutation = trpc.workOrderTemplate.duplicate.useMutation({
    onSuccess: (data) => {
      message.success(`テンプレートを複製しました: ${data.templateName}`);
      // 複製したテンプレートの詳細ページへ遷移
      navigate(`/app/work-order-templates/${data.id}`);
    },
    onError: (error) => {
      message.error(`複製に失敗しました: ${error.message}`);
    },
  });

  // ユーザー一覧を取得
  const { data: usersData } = trpc.user.list.useQuery(
    {
      companyId: user?.companyId,
      page: 1,
      limit: 200
    },
    {
      enabled: Boolean(user?.companyId) && isEditModalOpen
    }
  );

  useEffect(() => {
    void refetch();
  }, [id, refetch]);

  const template = templates.find((t) => t.id === id);

  const { data: workTypeCategories = [], isLoading: isLoadingWorkTypeCategories } =
    trpc.workTypeCategory.list.useQuery({
      includeInactive: false,
      sortBy: 'display_order',
      sortOrder: 'asc'
    });

  const normalizedWorkTypeCategories = useMemo(() => {
    const list = Array.isArray(workTypeCategories) ? workTypeCategories : [];
    return list.filter(
      (category): category is { id: string; categoryLabel?: string | null; color?: string | null; icon?: string | null } =>
        Boolean(category?.id),
    );
  }, [workTypeCategories]);

  const workTypeCategoryMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of normalizedWorkTypeCategories) {
      map.set(category.id, category.categoryLabel ?? '');
    }
    return map;
  }, [normalizedWorkTypeCategories]);

  const templateWithUserRelations = hasUserRelations(template) ? template : undefined;
  // ユーザーリストをSelectオプションに変換
  const rawUserList = Array.isArray(usersData?.data) ? usersData?.data : [];
  const userOptions = rawUserList
    .filter(isUserOptionSource)
    .map((user) => ({
      label: user.name ?? user.email ?? user.id,
      value: user.id
    }));

  const parseCommaSeparated = (value?: string) => {
    if (!value) {
      return undefined;
    }
    const entries = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    return entries.length > 0 ? entries : undefined;
  };

  const handleDelete = () => {
    if (!template) return;

    Modal.confirm({
      title: 'テンプレートを削除しますか？',
      content: `${template.templateName} を削除すると復元できません。`,
      okText: '削除する',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => {
        deleteTemplate(template.id);
        navigate('/app/work-order-templates');
      }
    });
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'duplicate',
      label: canEdit ? '複製' : (
        <span title={getDisabledMessage('edit')}>複製</span>
      ),
      icon: canEdit ? <CopyOutlined /> : <LockOutlined />,
      onClick: () => {
        if (template && canEdit) {
          duplicateMutation.mutate({ id: template.id });
        }
      },
      disabled: duplicateMutation.isPending || !canEdit,
    },
    { type: 'divider' as const },
    {
      key: 'delete',
      label: canManage ? '削除' : (
        <span title={getDisabledMessage('manage')}>削除</span>
      ),
      icon: canManage ? <DeleteOutlined /> : <LockOutlined />,
      danger: canManage,
      onClick: canManage ? handleDelete : undefined,
      disabled: !canManage
    }
  ];

  const resolvedWorkTypeLabel = useMemo(() => {
    if (!template) {
      return null;
    }
    if (template.workTypeCategory?.categoryLabel) {
      return template.workTypeCategory.categoryLabel;
    }
    if (template.workTypeCategoryId) {
      const mapped = workTypeCategoryMap.get(template.workTypeCategoryId) ?? null;
      if (mapped) {
        return mapped;
      }
    }
    if ('workType' in template) {
      const workType = (template as { workType?: unknown }).workType;
      if (typeof workType === 'string') {
        return WORK_TYPE_LABEL[workType] ?? workType;
      }
    }
    return null;
  }, [template, workTypeCategoryMap]);

  useEffect(() => {
    if (isEditModalOpen && template) {
      form.setFieldsValue({
        templateName: template.templateName,
        description: template.description ?? undefined,
        orderIndex: template.orderIndex ?? 0,
        workTypeCategoryId: template.workTypeCategoryId ?? undefined,
        priority: template.priority,
        instructions: template.instructions ?? undefined,
        safetyNotes: template.safetyNotes ?? undefined,
        standardHours: template.standardHours ?? undefined,
        requiredTools: template.requiredTools?.join(', ') ?? undefined,
        requiredParts: template.requiredParts?.join(', ') ?? undefined,
        requiresApproval: template.requiresApproval ?? false,
        approverUserIds: template.approvalSettings?.approverUserIds ?? [],
        approverRoles: template.approvalSettings?.approverRoles ?? []
      });
      setChecklistItems((template.checklistItems as ChecklistItem[]) || []);
    }
  }, [isEditModalOpen, template, form]);

  const handleEditSubmit = async () => {
    if (!template) return;

    const values = await form.validateFields();

    // 承認設定の構築
    const approvalSettings = values.requiresApproval ? {
      approverUserIds: values.approverUserIds && values.approverUserIds.length > 0 ? values.approverUserIds : undefined,
      approverRoles: values.approverRoles && values.approverRoles.length > 0 ? values.approverRoles : undefined
    } : undefined;

    const payload = {
      id: template.id,
      templateName: values.templateName,
      description: values.description?.trim() ? values.description.trim() : undefined,
      orderIndex: typeof values.orderIndex === 'number' ? values.orderIndex : 0,
      workTypeCategoryId: values.workTypeCategoryId ?? undefined,
      priority: values.priority,
      instructions: values.instructions?.trim() ? values.instructions.trim() : undefined,
      safetyNotes: values.safetyNotes?.trim() ? values.safetyNotes.trim() : undefined,
      standardHours: typeof values.standardHours === 'number' ? values.standardHours : undefined,
      requiredTools: parseCommaSeparated(values.requiredTools),
      requiredParts: parseCommaSeparated(values.requiredParts),
      isActive: true,
      checklistItems: checklistItems.length > 0 ? checklistItems : undefined,
      requiresApproval: values.requiresApproval ?? false,
      approvalSettings
    };

    updateTemplate(payload);
    clearBackup(); // 保存成功時にバックアップをクリア
    setIsEditModalOpen(false);
    setTimeout(() => {
      void refetch();
    }, 500);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!template) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="作業指示書が見つかりません"
          description="指定された作業指示書が存在しないか、削除された可能性があります。"
          type="warning"
          showIcon
          action={
            <Button onClick={() => navigate('/app/work-order-templates')}>
              一覧に戻る
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            {
              title: (
                <a onClick={() => navigate('/app/work-order-templates')}>
                  作業指示書
                </a>
              )
            },
            {
              title: template.templateName
            }
          ]}
        />

        <Card
          title={
            <Space>
              <FileTextOutlined />
              <Title level={3} style={{ margin: 0 }}>
                {template.templateName}
              </Title>
            </Space>
          }
          extra={
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/app/work-order-templates')}
              >
                一覧に戻る
              </Button>
              <PermissionButton
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsEditModalOpen(true)}
                requiredPermission="edit"
              >
                編集
              </PermissionButton>
              <Dropdown menu={{ items: menuItems }} placement="bottomRight">
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space size="middle" wrap>
              <Tag color="geekblue">#{template.displayId}</Tag>
              <Tag color={PRIORITY_COLOR[template.priority] ?? 'default'}>
                優先度: {PRIORITY_LABEL[template.priority] ?? template.priority}
              </Tag>
              <Tag icon={<ToolOutlined />}>
                {resolvedWorkTypeLabel ?? '-'}
              </Tag>
            </Space>

            <Descriptions bordered column={2}>
              <Descriptions.Item label="表示順" span={1}>
                {template.orderIndex ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="作業カテゴリ" span={1}>
                {resolvedWorkTypeLabel ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="優先度" span={1}>
                {PRIORITY_LABEL[template.priority] ?? template.priority}
              </Descriptions.Item>
              <Descriptions.Item label="標準作業時間" span={1}>
                {template.standardHours ? `${template.standardHours} 時間` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="作成者" span={1}>
                {templateWithUserRelations?.createdByUser ? (
                  <UserDisplay user={templateWithUserRelations.createdByUser ?? undefined} />
                ) : (
                  <Typography.Text type="secondary">不明</Typography.Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="最終編集者" span={1}>
                {templateWithUserRelations?.updatedByUser ? (
                  <UserDisplay user={templateWithUserRelations.updatedByUser ?? undefined} />
                ) : (
                  <Typography.Text type="secondary">未編集</Typography.Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="作成日時" span={1}>
                {template.createdAt
                  ? dayjs(template.createdAt).format('YYYY/MM/DD HH:mm')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="更新日時" span={1}>
                {template.updatedAt
                  ? dayjs(template.updatedAt).format('YYYY/MM/DD HH:mm')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>

            {template.description && (
              <Card size="small" title="説明">
                <Paragraph>{template.description}</Paragraph>
              </Card>
            )}

            {template.instructions && (
              <Card size="small" title="作業指示書">
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                  {template.instructions}
                </Paragraph>
              </Card>
            )}

            {template.safetyNotes && (
              <Card size="small" title="安全注意事項">
                <Alert
                  message={template.safetyNotes}
                  type="warning"
                  showIcon
                />
              </Card>
            )}

            {template.requiredTools && template.requiredTools.length > 0 && (
              <Card size="small" title="必要ツール">
                <List
                  dataSource={template.requiredTools as string[]}
                  renderItem={(tool: string) => (
                    <List.Item>
                      <ToolOutlined /> {String(tool)}
                    </List.Item>
                  )}
                />
              </Card>
            )}

            {template.requiredParts && template.requiredParts.length > 0 && (
              <Card size="small" title="必要パーツ">
                <List
                  dataSource={template.requiredParts as string[]}
                  renderItem={(part: string) => (
                    <List.Item>
                      <CheckCircleOutlined /> {String(part)}
                    </List.Item>
                  )}
                />
              </Card>
            )}

            {template.checklistItems && template.checklistItems.length > 0 && (
              <Card size="small" title="チェックリスト">
                <List
                  dataSource={template.checklistItems as ChecklistItem[]}
                  renderItem={(item: ChecklistItem, index: number) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <Space>
                            <span>#{index + 1}</span>
                            <span>{item.title}</span>
                          </Space>
                        }
                        description={
                          <Space direction="vertical" size={0}>
                            {item.description && (
                              <Text type="secondary">{item.description}</Text>
                            )}
                            <Space size="small" wrap>
                              <Tag color="blue">
                                {CHECKLIST_ITEM_TYPE_LABEL[item.type] ?? item.type}
                              </Tag>
                              {item.required && <Tag color="red">必須</Tag>}
                              {item.type === 'select' && item.options && item.options.length > 0 && (
                                <Tag>選択肢: {item.options.join(', ')}</Tag>
                              )}
                            </Space>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </Space>
        </Card>

        {/* 部品・消耗品セクション */}
        <TemplatePartsSection templateId={id!} companyId={user?.companyId} />

        {/* ツールセクション */}
        <TemplateToolsSection templateId={id!} />
      </Space>

      <Modal
        title="作業指示書テンプレート編集"
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onOk={() => void handleEditSubmit()}
        okButtonProps={{
          loading: isUpdating
        }}
        destroyOnHidden
        width={720}
      >
        <WorkOrderTemplateForm
          form={form}
          checklistItems={checklistItems}
          onChecklistItemsChange={setChecklistItems}
          workTypeCategories={normalizedWorkTypeCategories}
          isLoadingWorkTypeCategories={isLoadingWorkTypeCategories}
          showApprovalSettings={true}
          userOptions={userOptions}
        />
      </Modal>
    </div>
  );
}

